import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import Footer from './Footer';
import SEOHead from './SEOHead';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <SEOHead
        title="Privacy Policy"
        description="Nerd Nation's privacy policy — how we collect, use and protect your personal data as a student on our platform."
        path="/privacy-policy"
      />
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-10">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Content Sections */}
          <div className="prose prose-slate max-w-none">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Welcome to Nerd Nation ("we," "our," or "us"). We are operated by <strong>SHRIKAMALATMIKA EDTECH PRIVATE LIMITED</strong>, 
                a company committed to providing quality educational services and protecting your privacy.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                <a href="https://nerdnation.world" className="text-blue-600 hover:text-blue-700"> nerdnation.world</a>, use our mobile application, 
                or engage with our services. Please read this privacy policy carefully.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using our services, you agree to the collection and use of information in accordance with this policy. 
                If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">We collect personal information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
                <li><strong>Profile Information:</strong> Educational background, institution name, course preferences</li>
                <li><strong>Payment Information:</strong> Processed securely through Razorpay (we do not store complete card details)</li>
                <li><strong>Authentication Data:</strong> Google account information (if you choose Google Sign-In)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Usage Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">We automatically collect certain information when you use our services:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent, interaction patterns</li>
                <li><strong>AI Credit Usage:</strong> Credits consumed, features accessed, transaction history</li>
                <li><strong>Learning Data:</strong> Course enrollments, exam attempts, progress tracking</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Content You Provide</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Messages to AI advisors and chatbots</li>
                <li>Uploaded files for deepfake detection (Samhara AI)</li>
                <li>Chat messages with friends/community</li>
                <li>Course submissions and assignments</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">We use the collected information for various purposes:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Service Delivery:</strong> To provide, maintain, and improve our educational services</li>
                <li><strong>Personalization:</strong> To customize your learning experience based on your preferences</li>
                <li><strong>AI Features:</strong> To power AI-based tools like study advisors, deepfake detection, and content generation</li>
                <li><strong>Communication:</strong> To send you updates, notifications, and educational content</li>
                <li><strong>Payment Processing:</strong> To process transactions and manage your credits</li>
                <li><strong>Analytics:</strong> To understand usage patterns and improve our platform</li>
                <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security incidents</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
              </ul>
            </section>

            {/* Third-Party Services */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use trusted third-party services to provide and enhance our platform:
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">Payment Processing:</h4>
                <p className="text-gray-700"><strong>Razorpay</strong> - Secure payment gateway for transactions</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">Authentication:</h4>
                <p className="text-gray-700"><strong>Google OAuth</strong> - For Google Sign-In functionality</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">AI Services:</h4>
                <p className="text-gray-700"><strong>OpenAI, Anthropic, Google Gemini</strong> - For AI-powered features</p>
                <p className="text-gray-700"><strong>BitMind AI</strong> - For deepfake detection (Samhara AI)</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">Database & Hosting:</h4>
                <p className="text-gray-700"><strong>MongoDB Atlas</strong> - Secure database storage</p>
                <p className="text-gray-700"><strong>Vercel, Render</strong> - Cloud hosting services</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">Real-time Communication:</h4>
                <p className="text-gray-700"><strong>Supabase</strong> - For real-time chat features</p>
              </div>

              <p className="text-gray-700 leading-relaxed mt-4">
                These third-party services have their own privacy policies. We encourage you to review their policies to understand 
                how they handle your data.
              </p>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Encryption:</strong> All data transmission is encrypted using HTTPS/SSL</li>
                <li><strong>Secure Storage:</strong> Personal data is stored in secure, encrypted databases</li>
                <li><strong>Access Controls:</strong> Limited access to personal information on a need-to-know basis</li>
                <li><strong>Regular Updates:</strong> We maintain up-to-date security patches and protocols</li>
                <li><strong>Payment Security:</strong> We use PCI-DSS compliant payment processors</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use 
                commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-700 leading-relaxed mb-3">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                <li><strong>Data Portability:</strong> Request transfer of your data to another service provider</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Account Deletion:</strong> Delete your account through settings or by contacting us</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at the email address provided in the Contact section.
              </p>
            </section>

            {/* Cookies and Tracking */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                <li><strong>Authentication Cookies:</strong> To keep you logged in</li>
                <li><strong>Preference Cookies:</strong> To remember your settings and preferences</li>
                <li><strong>Analytics Cookies:</strong> To understand how you use our platform</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our services.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                When your information is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            {/* Age Restriction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Age Restriction</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Our services are intended for users aged <strong>18 years and above</strong>. We do not knowingly collect personal 
                information from individuals under 18.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If you are under 18 years of age, please do not use our services or provide any personal information. 
                If you are a parent or guardian and believe your child has provided us with personal information, 
                please contact us, and we will take steps to delete such information.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may update this Privacy Policy from time to time. We will notify you of any changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Posting the new Privacy Policy on this page</li>
                <li>Updating the "Last updated" date</li>
                <li>Sending an email notification for significant changes</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">SHRIKAMALATMIKA EDTECH PRIVATE LIMITED</h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Email:</strong> <a href="mailto:support@nerdindians.com" className="text-blue-600 hover:text-blue-700">support@nerdindians.com</a></p>
                  <p><strong>Website:</strong> <a href="https://nerdnation.world" className="text-blue-600 hover:text-blue-700">https://nerdnation.world</a></p>
                  <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
                </div>
              </div>
            </section>

            {/* Consent */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Your Consent</h2>
              <p className="text-gray-700 leading-relaxed">
                By using our website, mobile application, or services, you consent to our Privacy Policy and agree to its terms.
              </p>
            </section>

          </div>

          {/* Footer Note */}
          <div className="mt-12 pt-6 border-t border-gray-200">
            <p className="text-center text-gray-600 text-sm">
              This Privacy Policy is effective as of the date stated above and will remain in effect except with respect to any 
              changes in its provisions in the future, which will be in effect immediately after being posted on this page.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
